// Auftrags-Domäne — Status-Zustandsautomat, finished_at-Lebenszyklus (Fix B-6),
// Überfällig-/Heute-fertig-Prädikate und die Teile-/Aufgaben-getriebene Status-Automatik.
// Portiert aus patchOrder / patchTask / syncOrderFromParts des Altcodes, dabei B-6 repariert.

import { localDayKey, DEFAULT_TIME_ZONE } from './time'

export const ORDER_STATUSES = ['offen', 'arbeit', 'wartet', 'fertig', 'abgeholt'] as const
export type OrderStatus = (typeof ORDER_STATUSES)[number]

export const ORDER_PRIOS = ['niedrig', 'mittel', 'hoch'] as const
export type OrderPrio = (typeof ORDER_PRIOS)[number]

export const ESTIMATE_STATUSES = ['none', 'sent', 'approved', 'declined'] as const
export type EstimateStatus = (typeof ESTIMATE_STATUSES)[number]

export const PART_STATES = ['needed', 'ordered', 'delivered', 'installed'] as const
export type PartState = (typeof PART_STATES)[number]

/** Abgeschlossene Status (zählen nicht als „offen/aktiv"). */
export const COMPLETED_STATUSES: readonly OrderStatus[] = ['fertig', 'abgeholt']

export function isOrderStatus(value: unknown): value is OrderStatus {
  return typeof value === 'string' && (ORDER_STATUSES as readonly string[]).includes(value)
}
export function isOrderPrio(value: unknown): value is OrderPrio {
  return typeof value === 'string' && (ORDER_PRIOS as readonly string[]).includes(value)
}
export function isPartState(value: unknown): value is PartState {
  return typeof value === 'string' && (PART_STATES as readonly string[]).includes(value)
}
export function isCompleted(status: OrderStatus | string): boolean {
  return (COMPLETED_STATUSES as readonly string[]).includes(status)
}
export function isEstimateStatus(value: unknown): value is EstimateStatus {
  return typeof value === 'string' && (ESTIMATE_STATUSES as readonly string[]).includes(value)
}

/** Zahlungsarten für eine bezahlte Rechnung (Alt-Parität: bar/ueberweisung/karte/sonstige). */
export const PAID_METHODS = ['bar', 'ueberweisung', 'karte', 'sonstige'] as const
export type PaidMethod = (typeof PAID_METHODS)[number]
export function isPaidMethod(value: unknown): value is PaidMethod {
  return typeof value === 'string' && (PAID_METHODS as readonly string[]).includes(value)
}

/** §14-Rechnungsnummer im Alt-Format `YYYY-NNNN` (je Werkstatt+Jahr fortlaufend, 4-stellig aufgefüllt). */
export function formatInvoiceNo(year: number, seq: number): string {
  return `${year}-${String(seq).padStart(4, '0')}`
}

/**
 * Erlaubte Status-Übergänge (Zustandsautomat). Selbst-Übergang ist erlaubt (idempotenter No-Op).
 * Bewusst gesperrt sind unsinnige Sprünge wie offen→abgeholt (ohne Arbeit) oder abgeholt→offen.
 * Ein unerlaubter Übergang ist ein Zustands-Konflikt → die API antwortet darauf mit 409.
 */
export const ORDER_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  offen: ['offen', 'arbeit', 'wartet', 'fertig'],
  arbeit: ['arbeit', 'offen', 'wartet', 'fertig'],
  wartet: ['wartet', 'offen', 'arbeit', 'fertig'],
  fertig: ['fertig', 'arbeit', 'wartet', 'abgeholt'],
  abgeholt: ['abgeholt', 'fertig'],
}

export function canTransition(from: OrderStatus | string, to: OrderStatus): boolean {
  const allowed = (ORDER_TRANSITIONS as Record<string, OrderStatus[]>)[from]
  return allowed ? allowed.includes(to) : false
}

/** Minimaler Zustand eines Auftrags für die reine Status-Logik. finishedAt als epoch ms (null = nicht fertig). */
export interface OrderState {
  status: OrderStatus
  finishedAt: number | null
}

/**
 * Wendet einen Status-Wechsel an und pflegt finished_at korrekt.
 *
 * FIX B-6: finished_at wird NUR gesetzt, wenn der Auftrag NEU auf 'fertig' wechselt (idempotent).
 * Ein erneuter 'fertig'-Klick auf einen bereits fertigen Auftrag lässt das ursprüngliche
 * Fertig-Datum unverändert (im Altcode wurde es bei jedem Klick auf „jetzt" überschrieben →
 * verfälschte „Heute fertig" + Offene-Posten-Sortierung).
 *
 * Rückwärtsschritt (offen/arbeit/wartet) setzt finished_at zurück. 'abgeholt' behält es.
 */
export function applyStatusChange(current: OrderState, next: OrderStatus, nowMs: number): OrderState {
  if (next === 'fertig') {
    return { status: 'fertig', finishedAt: current.status === 'fertig' ? current.finishedAt : nowMs }
  }
  if (next === 'offen' || next === 'arbeit' || next === 'wartet') {
    return { status: next, finishedAt: null }
  }
  // 'abgeholt': abgeschlossen — Fertig-Datum bleibt erhalten.
  return { status: 'abgeholt', finishedAt: current.finishedAt }
}

/**
 * Auftrags-Status aus dem Aufgaben-Stand ableiten (Altcode patchTask):
 * alle Aufgaben erledigt → 'fertig' (nur aus offen/arbeit); sonst offen → arbeit.
 * Nutzt applyStatusChange, daher ebenfalls idempotent bzgl. finished_at.
 */
export function deriveStatusFromTasks(
  current: OrderState,
  taskTotal: number,
  taskDone: number,
  nowMs: number,
): OrderState {
  if (taskTotal > 0 && taskDone >= taskTotal) {
    if (current.status === 'offen' || current.status === 'arbeit') return applyStatusChange(current, 'fertig', nowMs)
    return current
  }
  if (current.status === 'offen') return applyStatusChange(current, 'arbeit', nowMs)
  return current
}

/**
 * Auftrags-Status aus der Teile-Lage ableiten (Altcode syncOrderFromParts):
 * mind. ein Teil 'ordered' → 'wartet' (aus offen/arbeit); alle geliefert/verbaut → 'arbeit' (aus wartet).
 * 'fertig'/'abgeholt' werden NIE angefasst.
 */
export function deriveStatusFromParts(
  current: OrderState,
  parts: ReadonlyArray<{ status: PartState | string }>,
): OrderState {
  if (parts.length === 0) return current
  const anyOrdered = parts.some((p) => p.status === 'ordered')
  const anyPending = parts.some((p) => p.status === 'needed' || p.status === 'ordered')
  if (anyOrdered) {
    if (current.status === 'offen' || current.status === 'arbeit') return { status: 'wartet', finishedAt: current.finishedAt }
    return current
  }
  if (!anyPending) {
    if (current.status === 'wartet') return { status: 'arbeit', finishedAt: current.finishedAt }
    return current
  }
  return current
}

/** Zuweisung eines Mechanikers schiebt einen offenen Auftrag auf 'arbeit' (Altcode patchOrder). */
export function deriveStatusFromAssignment(current: OrderState, hasMechanic: boolean): OrderState {
  if (hasMechanic && current.status === 'offen') return { status: 'arbeit', finishedAt: current.finishedAt }
  return current
}

// ---- Filter-Prädikate (Übersicht/Dashboard) ----

/** Überfällig: geplant vor heute UND nicht abgeschlossen. `today` und `scheduledDate` als lokale 'YYYY-MM-DD'. */
export function isOverdue(
  order: { status: OrderStatus | string; scheduledDate?: string | null },
  today: string,
): boolean {
  return !!order.scheduledDate && order.scheduledDate < today && !isCompleted(order.status)
}

/**
 * „Heute fertiggestellt": finished_at fällt auf den heutigen LOKALEN Kalendertag.
 * finishedAtMs als absoluter Zeitstempel (epoch ms) — zeitzonenkorrekt (behebt nebenbei den
 * latenten UTC-Präfix-Vergleich des Altcodes).
 */
export function isDoneToday(
  finishedAtMs: number | null | undefined,
  nowMs: number,
  timeZone: string = DEFAULT_TIME_ZONE,
): boolean {
  if (finishedAtMs == null) return false
  return localDayKey(finishedAtMs, timeZone) === localDayKey(nowMs, timeZone)
}
